/** Observe a paging boundary without requiring a load-more decision. Intersection
 * observation is preferred; passive scroll/resize plus one animation frame is the
 * fallback. A stopped, hidden or stale surface never starts another request. */
export function observeContinuation(
  node,
  load,
  { platform = globalThis, active = () => true, margin = 700 } = {},
) {
  let stopped = false,
    running = false,
    queued = false;
  const visible = () => !platform.document?.hidden;
  const near = () => {
    const bounds = node.getBoundingClientRect?.();
    return bounds && bounds.top <= (platform.innerHeight ?? 0) + margin && bounds.bottom >= -margin;
  };
  const run = () => {
    if (stopped || running || !active() || !visible()) return;
    running = true;
    let task;
    try {
      task = load();
    } catch {
      running = false;
      return;
    }
    Promise.resolve(task)
      .catch(() => {})
      .finally(() => {
        running = false;
        // A new paging boundary is installed by its canonical owner after success.
      });
  };
  const frame = platform.requestAnimationFrame ?? ((callback) => setTimeout(callback, 16));
  const schedule = () => {
    if (queued || stopped) return;
    queued = true;
    frame(() => {
      queued = false;
      if (near()) run();
    });
  };
  let observer;
  if (platform.IntersectionObserver) {
    observer = new platform.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) run();
      },
      { rootMargin: `0px 0px ${margin}px 0px`, threshold: 0 },
    );
    observer.observe(node);
  } else {
    platform.addEventListener?.('scroll', schedule, { passive: true });
    platform.addEventListener?.('resize', schedule, { passive: true });
    schedule();
  }
  const resume = () => {
    if (!visible() || stopped || !active()) return;
    if (observer) {
      observer.unobserve?.(node);
      observer.observe(node);
    } else schedule();
  };
  platform.document?.addEventListener?.('visibilitychange', resume);
  return {
    disconnect() {
      stopped = true;
      observer?.disconnect();
      platform.removeEventListener?.('scroll', schedule);
      platform.removeEventListener?.('resize', schedule);
      platform.document?.removeEventListener?.('visibilitychange', resume);
    },
  };
}
